// client/src/utils/outboxIdentity.js
// Identity + scope capture for queued offline writes.
//
// Two things have to be pinned at the moment a write is queued, not at the moment
// it is sent, because the device may be shared and the user may switch context
// before reconnecting:
//   • who queued it  — so a replay can't be attributed to whoever is logged in later
//   • what scope it was made in — so marks entered for one school/year/phase can't
//     land in another after the user switches.

// Token keys in the same priority order fetchJson uses, so the identity recorded
// is the identity the request would actually be sent under (impersonation included).
const TOKEN_KEYS = ['impersonationToken', 'accessToken', 'token'];

// Scope headers fetchJson injects. It only fills these when absent, so replaying a
// write with them set explicitly overrides whatever the current session holds.
export const SCOPE_HEADERS = [
  'X-School-Id',
  'X-Scope-School-Id',
  'X-Academic-Year-Id',
  'X-Active-Phase',
  'X-Active-Phase-Id',
];

const SCOPE_STORAGE_KEYS = {
  'X-School-Id': ['activeSchoolId', 'schoolId', 'scopeSchoolId'],
  'X-Scope-School-Id': ['scopeSchoolId', 'activeSchoolId', 'schoolId'],
  'X-Academic-Year-Id': ['currentAcademicYearId', 'academicYearId'],
  'X-Active-Phase': ['schoolAdmin_activePhase', 'activePhase'],
  'X-Active-Phase-Id': ['schoolAdmin_activePhaseId', 'activePhaseId'],
};

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

function decodeJwtId(token) {
  try {
    const parts = String(token).split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload && payload.id != null ? String(payload.id) : null;
  } catch (_) {
    return null;
  }
}

// Who is logged in right now, per the token that will carry the request.
export function currentIdentityId() {
  for (const key of TOKEN_KEYS) {
    const raw = readStorage(key);
    if (!raw) continue;
    const id = decodeJwtId(raw);
    if (id) return id;
  }
  return null;
}

// Snapshot the school / academic-year / phase context the user is working in.
export function captureScope() {
  const scope = {};
  for (const header of SCOPE_HEADERS) {
    for (const storageKey of SCOPE_STORAGE_KEYS[header]) {
      const value = readStorage(storageKey);
      if (value) {
        scope[header] = value;
        break;
      }
    }
  }
  return scope;
}

// A key that identifies one logical write across every retry of it. Generated
// once at enqueue time and never regenerated — that stability is exactly what
// lets the server collapse duplicate replays.
export function newIdempotencyKey() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (_) { /* fall through */ }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
}
